import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './QuestionnaireForm.css';

// ─── Survey Data (from survey.md) ────────────────────────────────────────────
const steps = [
  {
    id: 'q1',
    section: 'Section 1: Visual Priorities & Daily Activities',
    title: 'What distances are most critical for your daily activities and hobbies?',
    subtitle: 'Check all that apply.',
    type: 'checkbox',
    field: 'criticalDistances',
    options: [
      {
        value: 'distance',
        label: 'Distance (Far)',
        detail: 'Driving, watching TV, playing golf, enjoying scenery.',
      },
      {
        value: 'intermediate',
        label: "Intermediate (Arm's Length)",
        detail: "Using a computer, looking at a car dashboard, cooking, reading sheet music, playing cards.",
      },
      {
        value: 'near',
        label: 'Near (Up Close)',
        detail: 'Reading books, looking at a smartphone, sewing, reading medicine bottles or fine print.',
      },
    ],
  },
  {
    id: 'q2',
    section: 'Section 1: Visual Priorities & Daily Activities',
    title: 'Which best describes your reading habits?',
    type: 'radio',
    field: 'readingHabits',
    options: [
      {
        value: 'minimal',
        label: 'Minimal reader',
        detail: 'I rarely read fine print; mostly I just read my phone or a restaurant menu.',
      },
      {
        value: 'occasional',
        label: 'Occasional reader',
        detail: "I read standard print occasionally but don't mind putting on reading glasses to do it.",
      },
      {
        value: 'avid',
        label: 'Avid reader / detail-oriented',
        detail: 'I am an avid reader or perform detailed up-close hobbies (e.g., sewing, model-building) and want to do this without glasses.',
      },
    ],
  },
  {
    id: 'q3',
    section: 'Section 2: Spectacle Independence',
    title: 'How do you feel about wearing glasses after cataract surgery?',
    type: 'radio',
    field: 'glassesPreference',
    options: [
      {
        value: 'fine',
        label: 'Completely fine with glasses',
        detail: 'I just want the sharpest, clearest vision possible, even if it means wearing glasses for reading and computer work.',
      },
      {
        value: 'mostly_free',
        label: 'Mostly free from glasses',
        detail: 'I would love to drive and use my computer without glasses, but I am perfectly fine buying over-the-counter reading glasses for very small print.',
      },
      {
        value: 'strongly_dislike',
        label: 'Strongly dislike glasses',
        detail: 'My primary goal is to be as free from glasses as possible for all distances (near, intermediate, and far).',
      },
    ],
  },
  {
    id: 'q4',
    section: 'Section 3: Night Vision & Optical Tolerance',
    title: 'How frequently do you drive at night, and how tolerant are you of visual disturbances?',
    type: 'radio',
    field: 'nightDriving',
    options: [
      {
        value: 'frequent_intolerant',
        label: 'Frequent driver — zero tolerance',
        detail: 'I drive at night frequently (e.g., commercial driver, active night life) and absolutely cannot tolerate halos, starbursts, or glare around headlights.',
      },
      {
        value: 'occasional_tolerant',
        label: 'Occasional driver — can tolerate mild halos',
        detail: 'I drive at night occasionally and could tolerate mild to moderate halos or glare if it meant I didn\'t have to wear glasses during the day.',
      },
      {
        value: 'rare',
        label: 'Rarely drive at night',
        detail: 'I rarely drive at night, so nighttime glare or halos would not significantly impact my quality of life.',
      },
    ],
  },
  {
    id: 'q5',
    section: 'Section 3: Night Vision & Optical Tolerance',
    title: 'How would you describe your personality regarding your vision?',
    type: 'radio',
    field: 'personality',
    options: [
      {
        value: 'perfectionist',
        label: 'Perfectionist',
        detail: 'I am a perfectionist and very detail-oriented; I notice every small visual imperfection.',
      },
      {
        value: 'adaptable',
        label: 'Adaptable & easy-going',
        detail: 'I am generally adaptable and easy-going; I can get used to a new way of seeing if it gives me overall freedom from glasses.',
      },
    ],
  },
  {
    id: 'q6',
    section: 'Section 4: Ocular Health & Anatomy',
    title: 'Have you been diagnosed with any of the following eye conditions?',
    subtitle: 'Check all that apply.',
    type: 'checkbox',
    field: 'eyeConditions',
    options: [
      {
        value: 'amd',
        label: 'Macular Degeneration (AMD) or other retinal diseases',
      },
      {
        value: 'glaucoma',
        label: 'Glaucoma or Ocular Hypertension',
      },
      {
        value: 'dryEye',
        label: 'Severe Dry Eye Syndrome or Ocular Surface Disease',
      },
      {
        value: 'corneal',
        label: 'Corneal irregularities (e.g., Keratoconus)',
      },
      {
        value: 'none',
        label: 'None of the above — my eyes are completely healthy aside from cataracts',
        exclusive: true,
      },
    ],
  },
  {
    id: 'q7',
    section: 'Section 4: Ocular Health & Anatomy',
    title: 'Have you ever had previous vision correction surgery?',
    type: 'radio',
    field: 'priorSurgery',
    options: [
      {
        value: 'yes',
        label: 'Yes',
        detail: 'LASIK, PRK, or Radial Keratotomy (RK).',
      },
      {
        value: 'no',
        label: 'No',
      },
    ],
  },
  {
    id: 'q8',
    section: 'Section 4: Ocular Health & Anatomy',
    title: 'Have you been told by an eye doctor that you have astigmatism?',
    type: 'radio',
    field: 'astigmatism',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
      { value: 'unsure', label: "I'm not sure" },
    ],
  },
  {
    id: 'q9',
    section: 'Section 5: Financial Considerations',
    title: 'What is your budget for this procedure?',
    subtitle: 'Medicare and most private insurance cover standard cataract surgery and a standard (monofocal) lens. Premium lenses require an out-of-pocket upgrade fee.',
    type: 'radio',
    field: 'budget',
    options: [
      {
        value: 'insurance_only',
        label: 'Insurance only',
        detail: 'I want my insurance to cover the procedure fully; I do not want to pay out-of-pocket for a premium lens.',
      },
      {
        value: 'willing_to_invest',
        label: 'Willing to invest out-of-pocket',
        detail: 'I am willing to invest out-of-pocket for a premium lens if it significantly reduces my need for glasses.',
      },
    ],
  },
];

// ─── Default form state ───────────────────────────────────────────────────────
const defaultFormData = {
  criticalDistances: { distance: false, intermediate: false, near: false },
  readingHabits: '',
  glassesPreference: '',
  nightDriving: '',
  personality: '',
  eyeConditions: { amd: false, glaucoma: false, dryEye: false, corneal: false, none: false },
  priorSurgery: '',
  astigmatism: '',
  budget: '',
};

// ─── Component ────────────────────────────────────────────────────────────────
const QuestionnaireForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(defaultFormData);

  const step = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleRadio = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckbox = (field, value, exclusive) => {
    setFormData(prev => {
      const group = { ...prev[field] };
      if (exclusive) {
        // Toggle exclusive option; clear all others
        const newVal = !group[value];
        const reset = Object.fromEntries(Object.keys(group).map(k => [k, false]));
        return { ...prev, [field]: { ...reset, [value]: newVal } };
      }
      // Clear exclusive option when selecting a non-exclusive one
      group[value] = !group[value];
      // find exclusive keys
      const exclusiveKeys = steps
        .find(s => s.field === field)
        ?.options.filter(o => o.exclusive)
        .map(o => o.value) ?? [];
      exclusiveKeys.forEach(k => { group[k] = false; });
      return { ...prev, [field]: group };
    });
  };

  const isStepAnswered = () => {
    const s = steps[currentStep];
    if (s.type === 'radio') return Boolean(formData[s.field]);
    if (s.type === 'checkbox') return Object.values(formData[s.field]).some(Boolean);
    return true;
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(c => c + 1);
      window.scrollTo(0, 0);
    } else {
      localStorage.setItem('questionnaireData', JSON.stringify(formData));
      navigate('/results');
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(c => c - 1);
      window.scrollTo(0, 0);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="questionnaire-page">
      <div className="container">
        <h1 className="section-title">Cataract IOL Lifestyle &amp; Preferences Survey</h1>
        <p className="section-subtitle">Help us find the perfect lens for your individual needs</p>

        {/* Progress */}
        <div className="vibrant-progress-wrapper">
          <div className="vibrant-progress-info">
            <div className="vibrant-progress-step-info">
              <span className="vibrant-step-number">Question {currentStep + 1} of {steps.length}</span>
              <span className="vibrant-step-percentage">{Math.round(progress)}% Complete</span>
            </div>
            <span className="vibrant-section-label">{step.section}</span>
          </div>
          <div className="vibrant-progress-track">
            <div className="vibrant-progress-fill" style={{ width: `${progress}%` }}>
              <div className="vibrant-progress-glow"></div>
            </div>
          </div>
        </div>

        {/* Question card */}
        <div className="question-card card">
          <h2 className="question-title">{step.title}</h2>
          {step.subtitle && <p className="question-subtitle">{step.subtitle}</p>}

          {/* Radio */}
          {step.type === 'radio' && (
            <div className="options-list">
              {step.options.map(opt => (
                <div
                  key={opt.value}
                  className={`option-item${formData[step.field] === opt.value ? ' selected' : ''}`}
                  onClick={() => handleRadio(step.field, opt.value)}
                >
                  <div className="vibrant-radio-indicator">
                    <div className="vibrant-radio-inner"></div>
                  </div>
                  <div className="option-content">
                    <span className="option-label">{opt.label}</span>
                    {opt.detail && <span className="option-detail">{opt.detail}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Checkbox */}
          {step.type === 'checkbox' && (
            <div className="options-list">
              {step.options.map(opt => (
                <div
                  key={opt.value}
                  className={`option-item${formData[step.field][opt.value] ? ' selected' : ''}`}
                  onClick={() => handleCheckbox(step.field, opt.value, opt.exclusive)}
                >
                  <div className="vibrant-checkbox-indicator">
                    <span className="material-symbols-outlined">check</span>
                  </div>
                  <div className="option-content">
                    <span className="option-label">{opt.label}</span>
                    {opt.detail && <span className="option-detail">{opt.detail}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="questionnaire-actions">
          <button
            className="btn btn-secondary"
            onClick={handlePrevious}
            disabled={currentStep === 0}
          >
            ← Back
          </button>
          <button
            className="btn btn-primary"
            onClick={handleNext}
            disabled={!isStepAnswered()}
          >
            {currentStep < steps.length - 1 ? 'Next →' : 'Get My Recommendations'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireForm;
